import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { CheckCircle, X } from "lucide-react";
import { PricingPlan } from "../../services/adminPricingService";
import paymentService, { PaymentCancelledError, PaymentResult } from "../../services/paymentService";
import "./PlanCheckoutModal.css";

interface PlanCheckoutModalProps {
  plan: PricingPlan | null;
  onClose: () => void;
}

// Buy a plan without logging in first. The visitor gives the phone number and
// email the plan should belong to; after payment the plan is attached to the
// account with that number (now, or as soon as that number registers).
const PlanCheckoutModal: React.FC<PlanCheckoutModalProps> = ({ plan, onClose }) => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState<PaymentResult | null>(null);

  useEffect(() => {
    if (!plan) return;
    setError(null);
    setPaying(false);
    setResult(null);
  }, [plan]);

  useEffect(() => {
    if (!plan) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !paying) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [plan, paying, onClose]);

  if (!plan) return null;

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(phone)) {
      setError("Enter a valid 10-digit mobile number.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Enter a valid email address.");
      return;
    }
    setError(null);
    setPaying(true);
    try {
      setResult(await paymentService.payAsGuest(plan.id, phone, email.trim()));
    } catch (err) {
      if (!(err instanceof PaymentCancelledError)) {
        setError(err instanceof Error ? err.message : "Payment failed. Please try again.");
      }
    } finally {
      setPaying(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="pcm-overlay" onClick={() => !paying && onClose()}>
      <div className="pcm-dialog" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pcm-close" onClick={onClose} disabled={paying} aria-label="Close">
          <X size={18} />
        </button>

        {result ? (
          <div className="pcm-done">
            <CheckCircle className="pcm-done-icon" size={48} />
            <h3>Payment successful</h3>
            <p>
              Your <strong>{result.planName}</strong> plan is paid for.{" "}
              {result.hasAccount
                ? `It is now active on the account for +91 ${phone}. Log in with this number to start using it.`
                : `There is no account for +91 ${phone} yet. Register with this number and your plan will be activated automatically.`}
            </p>
            <button type="button" className="pcm-primary" onClick={() => navigate(result.hasAccount ? "/login" : "/register")}>
              {result.hasAccount ? "Go to login" : "Register now"}
            </button>
          </div>
        ) : (
          <form onSubmit={handlePay} noValidate>
            <h3>Buy {plan.name}</h3>
            <p className="pcm-price">
              {plan.price} <span>{plan.period}</span>
            </p>
            <p className="pcm-hint">
              Enter the mobile number and email of your account. We use them to give you access to the plan.
            </p>

            <label htmlFor="pcm-phone">Mobile number</label>
            <div className="pcm-phone-row">
              <span>+91</span>
              <input
                id="pcm-phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="10-digit number"
              />
            </div>

            <label htmlFor="pcm-email">Email</label>
            <input
              id="pcm-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />

            {error && <p className="pcm-error">{error}</p>}

            <button type="submit" className="pcm-primary" disabled={paying}>
              {paying ? "Processing..." : "Continue to payment"}
            </button>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PlanCheckoutModal;
